import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DownloadSimple, CircleNotch, Plus, Trash, ArrowRight, MapPin } from "@phosphor-icons/react";

const numInt = (v) => {
  const n = parseInt(String(v).trim(), 10);
  return Number.isNaN(n) ? null : n;
};

export const PointImport = ({ onUsePrompt }) => {
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState("PNEZD");
  const [pts, setPts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ranges, setRanges] = useState([{ from: "", to: "", layer: "BOUNDARY", closed: true, name: "" }]);
  const fileRef = useRef();
  const lastFile = useRef(null);

  const parseWith = async (file, ord) => {
    setBusy(true);
    const t = toast.loading("Parsing point file…");
    try {
      const d = await api.parsePoints(file, ord);
      setPts(d.points || []);
      setMeta({ count: d.count, min: d.min_num, max: d.max_num });
      if (d.min_num != null) {
        setRanges([{ from: String(d.min_num), to: String(d.max_num), layer: "BOUNDARY", closed: true, name: "" }]);
      }
      toast.success(`Parsed ${d.count} points (${d.min_num}–${d.max_num})`, { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse the point file.", { id: t });
    } finally { setBusy(false); }
  };

  const loadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    lastFile.current = file;
    await parseWith(file, order);
    e.target.value = "";
  };

  const changeOrder = (ord) => {
    setOrder(ord);
    if (lastFile.current) parseWith(lastFile.current, ord);
  };

  const editRange = (i, field, value) =>
    setRanges((rs) => rs.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  const addRange = () => setRanges((rs) => [...rs, { from: "", to: "", layer: "BOUNDARY", closed: true, name: "" }]);
  const delRange = (i) => setRanges((rs) => rs.filter((_, idx) => idx !== i));

  const rangePoints = (from, to) => {
    const f = numInt(from), tt = numInt(to);
    if (f == null || tt == null) return [];
    const lo = Math.min(f, tt), hi = Math.max(f, tt);
    return pts.filter((p) => { const n = numInt(p.num); return n != null && n >= lo && n <= hi; });
  };

  const useForGen = () => {
    const valid = ranges.filter((r) => numInt(r.from) != null && numInt(r.to) != null);
    if (!valid.length) { toast.error("Add at least one valid point range (from #, to #)."); return; }
    const rangeLines = valid.map((r, i) => {
      const rp = rangePoints(r.from, r.to);
      return `${i + 1}. Points ${numInt(r.from)}–${numInt(r.to)} (${rp.length} pts) -> ${r.closed ? "CLOSED" : "OPEN"} polyline on layer "${(r.layer || "BOUNDARY").trim()}"${r.name ? ` — ${r.name.trim()}` : ""}`;
    }).join("\n");

    // Embed the actual coordinates for referenced ranges (capped) so the routine can draw them precisely.
    const referenced = [];
    const seen = new Set();
    valid.forEach((r) => rangePoints(r.from, r.to).forEach((p) => {
      if (!seen.has(p.num)) { seen.add(p.num); referenced.push(p); }
    }));
    const capped = referenced.slice(0, 400);
    const ptLines = capped.map((p) => `P${p.num}: N=${p.n} E=${p.e}${p.z != null ? ` Z=${p.z}` : ""}${p.desc ? ` "${p.desc}"` : ""}`).join("\n");

    const prompt =
      `Import survey points from a ${order} CSV/TXT the user selects (columns: ${order === "PENZD" ? "Point,Easting,Northing,Elev,Desc" : "Point,Northing,Easting,Elev,Desc"}). ` +
      `Create each point on layer SURVEY-POINTS with a TEXT label (point number + description) and set PDMODE=34 / PDSIZE so points are visible.\n\n` +
      `THEN draw ONLY these selected point-number ranges, connecting the points in ascending point-number order:\n${rangeLines}\n\n` +
      `Draw each range as an LWPOLYLINE on its given layer (create the layer if absent), closing it when marked CLOSED, and report each polyline's length${valid.some((r) => r.closed) ? " and enclosed area" : ""}.\n\n` +
      `EXACT POINTS for the ranges above (draw straight to these coordinates so the boundary/structure is precise; the file selection can be used as a fallback):\n${ptLines}${referenced.length > capped.length ? "\n…(additional points continue in the file)" : ""}`;

    onUsePrompt(prompt);
    setOpen(false);
    toast.success(`Sent ${valid.length} range(s) to the generator prompt`);
  };

  const PALETTE = ["var(--primary)", "#e0af68", "#9ece6a", "#f7768e", "#7dcfff", "#bb9af7"];
  const sketch = () => {
    const valid = ranges
      .map((r) => ({ ...r, rp: [...rangePoints(r.from, r.to)].sort((a, b) => (numInt(a.num) - numInt(b.num))) }))
      .filter((r) => r.rp.length >= 1);
    const used = valid.length ? valid.flatMap((r) => r.rp) : pts.slice(0, 300);
    if (!used.length) return null;
    const ns = used.map((p) => p.n), es = used.map((p) => p.e);
    const minN = Math.min(...ns), maxN = Math.max(...ns), minE = Math.min(...es), maxE = Math.max(...es);
    const W = 440, H = 200, pad = 20;
    const spanE = (maxE - minE) || 1, spanN = (maxN - minN) || 1;
    const sc = Math.min((W - 2 * pad) / spanE, (H - 2 * pad) / spanN);
    const offX = (W - spanE * sc) / 2, offY = (H - spanN * sc) / 2;
    const x = (e) => offX + (e - minE) * sc;
    const y = (n) => H - offY - (n - minN) * sc;
    const labelAll = used.length <= 24;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxHeight: 210 }} data-testid="range-sketch-svg">
        {valid.map((r, i) => {
          const color = PALETTE[i % PALETTE.length];
          const pStr = r.rp.map((p) => `${x(p.e).toFixed(1)},${y(p.n).toFixed(1)}`).join(" ");
          return r.closed
            ? <polygon key={`s${i}`} points={pStr} fill={color} fillOpacity="0.08" stroke={color} strokeWidth="1.6" />
            : <polyline key={`s${i}`} points={pStr} fill="none" stroke={color} strokeWidth="1.6" />;
        })}
        {used.map((p, i) => (
          <g key={`p${i}`}>
            <circle cx={x(p.e)} cy={y(p.n)} r="2.6" fill="#8B8B99" />
            {labelAll && <text x={x(p.e) + 4} y={y(p.n) - 4} fill="#8B8B99" fontSize="8" fontFamily="monospace">{p.num}</text>}
          </g>
        ))}
      </svg>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="point-import-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <DownloadSimple size={14} weight="bold" /> Point Import
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="point-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><MapPin size={18} weight="bold" className="text-[var(--primary)]" />Point Import — draw boundaries/structures from #from–#to ranges</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Load a PNEZD/PENZD point file, then pick point-number ranges to connect into boundaries or structures on specific layers.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Select value={order} onValueChange={changeOrder}>
            <SelectTrigger className="h-8 w-[130px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" data-testid="point-order-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
              <SelectItem value="PNEZD" className="font-mono text-xs" data-testid="order-opt-PNEZD">PNEZD</SelectItem>
              <SelectItem value="PENZD" className="font-mono text-xs" data-testid="order-opt-PENZD">PENZD</SelectItem>
            </SelectContent>
          </Select>
          <input type="file" accept=".csv,.txt,text/*" ref={fileRef} className="hidden" onChange={loadFile} data-testid="point-file-input" />
          <Button size="sm" onClick={() => fileRef.current.click()} disabled={busy} data-testid="upload-points-btn"
            className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            {busy ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} />} Load CSV / TXT
          </Button>
          {meta && <span className="text-[11px] font-mono text-[var(--text-dim)]" data-testid="point-meta">{meta.count} pts · #{meta.min}–{meta.max}</span>}
        </div>

        {pts.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] flex items-center justify-between">
              <span>Ranges to draw (#from – #to)</span>
              <button onClick={addRange} data-testid="add-range-btn" className="flex items-center gap-1 text-[var(--primary)] hover:underline"><Plus size={12} weight="bold" /> add range</button>
            </div>
            <div className="flex flex-col gap-1.5 max-h-[190px] overflow-auto" data-testid="ranges-list">
              {ranges.map((r, i) => {
                const count = rangePoints(r.from, r.to).length;
                return (
                  <div key={i} className="grid grid-cols-[56px_56px_1fr_74px_66px_24px] gap-1.5 items-center" data-testid={`range-row-${i}`}>
                    <Input value={r.from} onChange={(e) => editRange(i, "from", e.target.value)} placeholder="from#" data-testid={`range-from-${i}`}
                      className="h-7 px-1.5 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                    <Input value={r.to} onChange={(e) => editRange(i, "to", e.target.value)} placeholder="to#" data-testid={`range-to-${i}`}
                      className="h-7 px-1.5 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                    <Input value={r.layer} onChange={(e) => editRange(i, "layer", e.target.value)} placeholder="layer" data-testid={`range-layer-${i}`}
                      className="h-7 px-1.5 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                    <button onClick={() => editRange(i, "closed", !r.closed)} data-testid={`range-closed-${i}`}
                      className={`h-7 rounded-[2px] border text-[10px] font-mono ${r.closed ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)]"}`}>
                      {r.closed ? "CLOSED" : "OPEN"}
                    </button>
                    <span className="text-[10px] font-mono text-[var(--text-dim)] text-center" data-testid={`range-count-${i}`}>{count} pts</span>
                    <button onClick={() => delRange(i)} data-testid={`range-del-${i}`} className="text-[var(--text-dim)] hover:text-[var(--alert)] flex justify-center"><Trash size={13} /></button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {pts.length > 0 && (
          <div className="border border-[var(--border)] rounded-[3px] bg-[var(--surface-2)] p-2" data-testid="range-sketch">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] mb-1">Preview — selected ranges</div>
            {sketch() || <div className="text-[11px] text-[var(--text-dim)] text-center py-6">Set a valid range to sketch it.</div>}
          </div>
        )}

        {pts.length > 0 && (
          <ScrollArea className="h-[110px] border border-[var(--border)] rounded-[3px]" data-testid="points-preview">
            <div className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] bg-[var(--surface-2)] sticky top-0 grid grid-cols-[50px_1fr_1fr_1fr] gap-2">
              <span>#</span><span>North</span><span>East</span><span>Desc</span>
            </div>
            {pts.slice(0, 200).map((p, i) => (
              <div key={i} className="grid grid-cols-[50px_1fr_1fr_1fr] gap-2 px-2 py-0.5 text-[11px] font-mono border-t border-[var(--border)]/40 text-[var(--text-dim)]">
                <span className="text-[var(--warn)]">{p.num}</span><span>{p.n}</span><span>{p.e}</span><span className="truncate">{p.desc || "—"}</span>
              </div>
            ))}
          </ScrollArea>
        )}

        {pts.length === 0 && (
          <div className="border border-dashed border-[var(--border)] rounded-[3px] p-6 text-center text-[12px] text-[var(--text-dim)]" data-testid="point-empty">
            Load a survey point file (e.g. <span className="font-mono">1,5000.00,5000.00,100.0,IP</span>) to pick point ranges.
          </div>
        )}

        {pts.length > 0 && (
          <Button onClick={useForGen} data-testid="use-points-btn"
            className="h-9 rounded-[3px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            <ArrowRight size={15} /> Use these ranges to generate LISP
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
